import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Button, Modal } from 'antd';
// Helpers
import {
  generateDefaultFormData,
  mapEditingItemToFormData,
  mapFormDataToCreateCampaignTaskV2Input,
  TimelineDeliverableExample,
  TimelineDeliverableFormData,
  validateStep1,
  validateStep2,
} from 'helpers/timelineDeliverables';
// Types
import { GetCampaignsV2_getCampaigns_entities_activities_SocialActivity_tasksV2 } from 'api/campaignV2/types/GetCampaignsV2';
import {
  CampaignMediaType,
  CampaignSmPlatform,
  CampaignTaskType,
  CreateCampaignTaskV2Input,
} from 'api/graphql-global-types';
// Components
import ContentSetup from './components/ContentSetup';
import PostDetails from './components/PostDetails';
// Utils
import { getMediaTypeOptions } from 'helpers/customCampaignConstants';
// Styles
import styles from './TimelineDeliverablesModal.module.scss';
import { errorNotification } from 'ui/Notification';

export const MAX_DESCRIPTION_LENGTH = 5000;
export const MAX_SUGGESTED_CAPTION_LENGTH = 5000;
export const MAX_TITLE_LENGTH = 5000;

type TimelineDeliverablesModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (
    data: CreateCampaignTaskV2Input,
    examples?: (TimelineDeliverableExample | File)[]
  ) => void;
  platform: CampaignSmPlatform;
  editingItem?: GetCampaignsV2_getCampaigns_entities_activities_SocialActivity_tasksV2 | null;
  /**
   * Custom campaigns let the admin pick the deliverable platform from the
   * campaign's selected platforms. When provided (and longer than one) a
   * platform selector is shown in the post step and drives the post-type list.
   */
  availablePlatforms?: CampaignSmPlatform[];
  /**
   * Custom campaign flow only. When true, the steps are reversed (choose
   * platform & post type first, then set up content pieces) and the content
   * piece media types are restricted by the selected post type. Other campaign
   * types keep the legacy order and free Video/Image choice.
   */
  customFlow?: boolean;
};

const TimelineDeliverablesModal: React.FC<TimelineDeliverablesModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  platform,
  editingItem,
  availablePlatforms,
  customFlow = false,
}) => {
  const [step, setStep] = useState<number>(1);
  const [selectedPlatform, setSelectedPlatform] =
    useState<CampaignSmPlatform>(platform);

  // Memoize defaultFormData to prevent infinite re-renders
  const defaultFormData = useMemo(
    () => generateDefaultFormData(selectedPlatform),
    [selectedPlatform]
  );

  // Keep original data safe - this never changes until submit
  const originalDataRef = useRef<TimelineDeliverableFormData | null>(null);

  // Initial state
  const [localFormData, setLocalFormData] =
    useState<TimelineDeliverableFormData>(
      () =>
        ({
          ...defaultFormData,
          ...(editingItem ? mapEditingItemToFormData(editingItem) : {}),
        } as TimelineDeliverableFormData)
    );

  useEffect(() => {
    if (isOpen) {
      const initialData = {
        ...defaultFormData,
        ...(editingItem ? mapEditingItemToFormData(editingItem) : {}),
      } as TimelineDeliverableFormData;

      originalDataRef.current = initialData;
      setLocalFormData(initialData);
      setStep(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, editingItem]);

  // When editing an existing task, derive its platform from the post type so
  // the platform selector and post-type list reflect the saved deliverable.
  useEffect(() => {
    if (!isOpen) return;
    const editingType = (editingItem?.type as string) || '';
    if (editingType.startsWith('TT')) {
      setSelectedPlatform(CampaignSmPlatform.TT);
    } else if (editingType.startsWith('IG')) {
      setSelectedPlatform(CampaignSmPlatform.IG);
    } else {
      setSelectedPlatform(platform);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, editingItem]);

  // Reset to proper initial data when closing without saving
  const handleCloseModal = () => {
    const initialData = {
      ...defaultFormData,
      ...(editingItem ? mapEditingItemToFormData(editingItem) : {}),
    } as TimelineDeliverableFormData;

    setLocalFormData(initialData);
    onClose();
    setStep(1);
  };

  // Validate whichever section is shown on the current step. The custom flow
  // shows Post Details on step 1 and Content on step 2; the legacy flow is the
  // reverse.
  const validateContentDetails = () => {
    if (validateStep1(localFormData)) return true;
    errorNotification('Please fill in all required fields in Content Details');
    return false;
  };

  const validatePostDetails = () => {
    if (validateStep2(localFormData)) return true;
    errorNotification('Please fill in all required fields in Post Details');
    return false;
  };

  const handleNext = () => {
    // Mirror the render logic below: step 1 shows Post Details in the custom
    // flow and Content otherwise; step 2 is reversed. Validate whichever
    // section is currently on screen.
    const validateCurrentStep = isStep1
      ? customFlow
        ? validatePostDetails
        : validateContentDetails
      : customFlow
      ? validateContentDetails
      : validatePostDetails;

    if (!validateCurrentStep()) return;

    if (isStep1) {
      setStep(2);
    } else {
      handleFormSubmit();
    }
  };

  const handleBack = () => {
    if (isStep2) {
      setStep(1);
    }
  };

  const handleFormSubmit = () => {
    const mappedData = mapFormDataToCreateCampaignTaskV2Input(localFormData);
    onSubmit(mappedData, customFlow ? localFormData.examples : undefined);
    handleCloseModal();
  };

  // Update local form data function - this updates the working copy
  const updateLocalFormData = (path: string, value: any) => {
    setLocalFormData((prev) => {
      const newData = { ...prev };
      const keys = path.split('.');
      let current: any = newData;

      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) {
          current[keys[i]] = {};
        }
        current = current[keys[i]];
      }

      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  const isStep1 = step === 1;
  const isStep2 = step === 2;

  // Custom flow: restrict the per-content-piece media types by the selected
  // post type (e.g. Reel/Live -> Video only). Legacy flow keeps Video + Image.
  const postType = localFormData.postDetails?.postType as
    | CampaignTaskType
    | undefined;
  const mediaTypeOptions = useMemo(
    () => (customFlow ? getMediaTypeOptions(postType) : undefined),
    [customFlow, postType]
  );

  // When the post type changes and a content piece's media type is no longer
  // allowed, reset it to the first allowed option. Custom flow only.
  useEffect(() => {
    if (!customFlow || !mediaTypeOptions) return;
    (localFormData.contentPieces || []).forEach((piece, index) => {
      if (
        piece?.mediaType &&
        !mediaTypeOptions.some((option) => option.value === piece.mediaType)
      ) {
        updateLocalFormData(
          `contentPieces.${index}.mediaType`,
          mediaTypeOptions[0].value as CampaignMediaType
        );
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postType, customFlow]);

  const contentSetupStep = (
    <ContentSetup
      formData={localFormData}
      updateNestedFormData={updateLocalFormData}
      mediaTypeOptions={mediaTypeOptions}
      showExampleFiles={customFlow}
    />
  );

  const postDetailsStep = (
    <PostDetails
      formData={localFormData}
      updateNestedFormData={updateLocalFormData}
      platform={selectedPlatform}
      availablePlatforms={availablePlatforms}
      onPlatformChange={setSelectedPlatform}
    />
  );

  return (
    <Modal
      title="Set Up Your Deliverable"
      visible={isOpen}
      onCancel={handleCloseModal}
      width={800}
      footer={null}
      className={styles.timelineDeliverablesModal}
    >
      <div className={styles.modalContent}>
        <div className={styles.mainContent}>
          {/* Custom flow reverses the order: post details first, then content. */}
          {isStep1 && (customFlow ? postDetailsStep : contentSetupStep)}

          {isStep2 && (customFlow ? contentSetupStep : postDetailsStep)}

          <div className={styles.actions}>
            {isStep2 && (
              <Button type="default" onClick={handleBack}>
                Back
              </Button>
            )}
            <Button type="primary" onClick={handleNext}>
              {isStep1 ? 'Next' : 'Done'}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default TimelineDeliverablesModal;
